import {
  TableCell,
  Table,
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  TableBody,
} from "@mui/material";
import { format, isToday } from "date-fns";
import { AppointmentData } from "hooks/useAppointments";
import { themeColors } from "theme/colorTokens";

export interface AppointmentDayViewProps {
  currentDate: Date;
  timeSlots: Date[];
  getAppointmentsForDay: (date: Date) => AppointmentData[];
  getAppointmentStyle: (appointment: AppointmentData) => React.CSSProperties;
  handleEditAppointment: (appointment: AppointmentData) => void;
}
const AppointmentDayView = ({
  currentDate,
  timeSlots,
  getAppointmentsForDay,
  getAppointmentStyle,
  handleEditAppointment,
}: AppointmentDayViewProps) => {
  return (
    <TableContainer
      component={Paper}
      sx={{
        height: "100%",
        "& .MuiTable-root": {
          tableLayout: "fixed",
        },
        display: "contents",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                width: "80px",
                padding: 0,
                backgroundColor: themeColors.background.paper,
                border: "none",
              }}
            ></TableCell>
            <TableCell
              sx={{
                textAlign: "center",
                padding: "12px",
                borderRadius: "12px",
                border: "none",
                backgroundColor: isToday(currentDate)
                  ? themeColors.background.highlight
                  : themeColors.background.paper,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: themeColors.text.primary }}
              >
                {format(currentDate, "EEEE")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: themeColors.text.secondary }}
              >
                {format(currentDate, "MMM d")}
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {timeSlots.map((time) => (
            <TableRow key={time.toISOString()} sx={{ height: "60px" }}>
              <TableCell
                sx={{
                  width: "80px",
                  height: "60px",
                  border: "none",
                  padding: 0,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: themeColors.text.secondary }}
                  >
                    {format(time, "h:mm a")}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  padding: 0,
                  position: "relative",
                  height: "60px",
                  border: "none",
                }}
              >
                <Box
                  sx={{
                    borderBottom: `1px solid ${themeColors.border.light}`,
                    padding: 0,
                    margin: 0,
                    zIndex: -1,
                  }}
                ></Box>
                <Box
                  sx={{
                    border: "none",
                    padding: 0,
                    margin: 0,
                    zIndex: -1,
                  }}
                ></Box>
                {getAppointmentsForDay(currentDate)
                  .filter((appointment) => {
                    if (!appointment.startTime) return false;
                    const appointmentHour = appointment.startTime.getHours();
                    const appointmentMinute =
                      appointment.startTime.getMinutes();
                    const slotHour = time.getHours();
                    const slotMinute = time.getMinutes();

                    // Check if appointment starts in this time slot
                    return (
                      appointmentHour === slotHour &&
                      appointmentMinute === slotMinute
                    );
                  })
                  .map((appointment) => (
                    <Box
                      key={appointment.id}
                      className="absolute rounded cursor-pointer"
                      style={{
                        ...getAppointmentStyle(appointment),
                        left: "1px",
                        right: "1px",
                      }}
                      onClick={() => handleEditAppointment(appointment)}
                      sx={{
                        "&:hover": {
                          opacity: 0.8,
                        },
                      }}
                    >
                      <Box sx={{ padding: "6px 8px" }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: themeColors.text.bold,
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            lineHeight: 1.2,
                            textAlign: "center",
                            display: "block",
                          }}
                        >
                          {appointment.patientName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: themeColors.text.secondary,
                            fontWeight: 400,
                            fontSize: "0.7rem",
                            lineHeight: 1.2,
                            display: "block",
                            textAlign: "center",
                          }}
                        >
                          {appointment.startTime
                            ? format(appointment.startTime, "h:mm a")
                            : "--"}{" "}
                          -{" "}
                          {appointment.endTime
                            ? format(appointment.endTime, "h:mm a")
                            : "--"}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AppointmentDayView;
